import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { apiUrl, CarId, PlayState } from "./protocol";
import { TranslationKey, useAppLanguage } from "./i18n";
import { buildRaceLineForecast, ForecastPoint } from "./race-line-forecast";
import type { Stage2TelemetryRecord } from "./stage2-telemetry-data";
import "./race-lines.css";

export type RaceLineMode = "actual" | "generated" | "both";

interface RaceLinePoint {
  race_time_ms: number;
  x_m: number;
  y_m: number;
  tick_index?: number;
  causal_id?: string;
  segment_break?: boolean;
}

interface RaceLineAsset {
  schema_version: "stage2-race-lines/v1";
  car_id: CarId;
  seek_generation: number;
  coordinate_frame: { id: string; unit: "m"; origin: string };
  course: {
    circuit: "suzuka";
    source: string;
    closed: true;
    lap_length_m: number;
    width: {
      source: "data/derived/suzuka-course-bounds-v1.json";
      lower_decile_m: number;
      median_m: number;
      upper_decile_m: number;
    };
    points: { x: number; y: number }[];
  };
  lap_ranges: {
    lap_counter: number;
    start_record_index: number;
    end_record_index: number;
    start_race_time_ms: number;
    end_race_time_ms: number;
  }[];
  actual: {
    source: "recorded-gps-full-race-trace";
    mutable: false;
    points: RaceLinePoint[];
  };
  generated: {
    source: "observation-azpnet-shadow-vehicle-state";
    presentation_mode: "recorded-pose-one-step-observation";
    missing_interval_policy: "break-subpath-above-500ms";
    intervention: false;
    overwrites_recorded_gps: false;
    points: RaceLinePoint[];
  };
}

const isPoint = (value: unknown): value is RaceLinePoint => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<RaceLinePoint>;
  return Number.isInteger(item.race_time_ms) && Number.isFinite(item.x_m) && Number.isFinite(item.y_m)
    && (item.segment_break === undefined || typeof item.segment_break === "boolean");
};

export const isRaceLineAsset = (
  value: unknown, carId: CarId, seekGeneration: number,
): value is RaceLineAsset => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<RaceLineAsset>;
  const ordered = (points: RaceLinePoint[] | undefined) => !!points?.length
    && points.every((point, index) => isPoint(point)
      && (index === 0 || point.race_time_ms > points[index - 1].race_time_ms));
  return item.schema_version === "stage2-race-lines/v1"
    && item.car_id === carId && item.seek_generation === seekGeneration
    && item.coordinate_frame?.unit === "m"
    && item.course?.circuit === "suzuka" && item.course.closed === true
    && item.course.width?.source === "data/derived/suzuka-course-bounds-v1.json"
    && Number.isFinite(item.course.width.lower_decile_m)
    && Number.isFinite(item.course.width.median_m)
    && Number.isFinite(item.course.width.upper_decile_m)
    && Array.isArray(item.course.points) && item.course.points.length >= 3
    && item.course.points.every(point => Number.isFinite(point.x) && Number.isFinite(point.y))
    && Array.isArray(item.lap_ranges) && item.lap_ranges.length > 0
    && item.actual?.source === "recorded-gps-full-race-trace"
    && item.actual.mutable === false && ordered(item.actual.points)
    && item.generated?.source === "observation-azpnet-shadow-vehicle-state"
    && item.generated.presentation_mode === "recorded-pose-one-step-observation"
    && item.generated.missing_interval_policy === "break-subpath-above-500ms"
    && item.generated.intervention === false
    && item.generated.overwrites_recorded_gps === false
    && ordered(item.generated.points);
};

export function useRaceLineAsset(carId: CarId, seekGeneration: number, dataFetchEnabled = true) {
  const { t } = useAppLanguage();
  const [asset, setAsset] = useState<RaceLineAsset | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    if (!dataFetchEnabled) return () => controller.abort();
    setAsset(null);
    setStatus("loading");
    setError("");
    fetch(apiUrl("/api/race/lines", { car_id: carId, seek_generation: seekGeneration, display: 1 }), {
      cache: "no-store", signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error(`${t("error.raceLineApi")}: ${response.status}`);
      const value = await response.json() as unknown;
      if (!isRaceLineAsset(value, carId, seekGeneration))
        throw new Error(t("error.raceLineIdentity"));
      if (!controller.signal.aborted) {
        setAsset(value);
        setStatus("ready");
      }
    }).catch(reason => {
      if (reason?.name !== "AbortError" && !controller.signal.aborted) {
        setError(String(reason));
        setStatus("error");
      }
    });
    return () => controller.abort();
  }, [carId, dataFetchEnabled, seekGeneration, t]);

  return { asset, status, error };
}

const lastPointIndexAtTime = (points: RaceLinePoint[], raceTimeMs: number): number => {
  if (!points.length || raceTimeMs < points[0].race_time_ms) return -1;
  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (points[middle].race_time_ms <= raceTimeMs) low = middle;
    else high = middle - 1;
  }
  return low;
};

interface Projection {
  point: (source: RaceLinePoint) => { x: number; y: number };
  scale: number;
}

interface CourseOverpass {
  points: { x: number; y: number }[];
  cutoutPoints: [{ x: number; y: number }, { x: number; y: number }];
  direction: { x: number; y: number };
}

const cross = (ax: number, ay: number, bx: number, by: number) => ax * by - ay * bx;

const findSuzukaOverpass = (points: { x: number; y: number }[]): CourseOverpass | null => {
  // Suzuka has one self-crossing. In lap order the later segment is the West
  // Straight bridge; the earlier segment is the T10 approach below it.
  for (let lower = 0; lower < points.length; lower += 1) {
    const lowerNext = (lower + 1) % points.length;
    const a = points[lower], b = points[lowerNext];
    for (let upper = lower + 2; upper < points.length; upper += 1) {
      const upperNext = (upper + 1) % points.length;
      if (lower === 0 && upperNext === 0) continue;
      const c = points[upper], d = points[upperNext];
      const rx = b.x - a.x, ry = b.y - a.y;
      const sx = d.x - c.x, sy = d.y - c.y;
      const denominator = cross(rx, ry, sx, sy);
      if (Math.abs(denominator) < 1e-9) continue;
      const qx = c.x - a.x, qy = c.y - a.y;
      const alongLower = cross(qx, qy, sx, sy) / denominator;
      const alongUpper = cross(qx, qy, rx, ry) / denominator;
      if (alongLower <= 0 || alongLower >= 1 || alongUpper <= 0 || alongUpper >= 1) continue;

      const bridgePoints: { x: number; y: number }[] = [];
      for (let offset = -4; offset <= 5; offset += 1)
        bridgePoints.push(points[(upper + offset + points.length) % points.length]);
      const first = bridgePoints[0], last = bridgePoints[bridgePoints.length - 1];
      const length = Math.hypot(last.x - first.x, last.y - first.y);
      if (length < 1e-9) return null;
      const localLength = Math.hypot(sx, sy);
      if (localLength < 1e-9) return null;
      const intersection = { x: a.x + alongLower * rx, y: a.y + alongLower * ry };
      const localDirection = { x: sx / localLength, y: sy / localLength };
      return {
        points: bridgePoints,
        // Erase only the crossing itself. The longer bridge path is redrawn on
        // top and merges into the unchanged road at both ends.
        cutoutPoints: [
          { x: intersection.x - localDirection.x * 22, y: intersection.y - localDirection.y * 22 },
          { x: intersection.x + localDirection.x * 22, y: intersection.y + localDirection.y * 22 },
        ],
        direction: { x: (last.x - first.x) / length, y: (last.y - first.y) / length },
      };
    }
  }
  return null;
};

const distanceToSegment = (
  point: { x: number; y: number }, start: { x: number; y: number }, end: { x: number; y: number },
) => {
  const dx = end.x - start.x, dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  const ratio = lengthSquared > 0
    ? Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared))
    : 0;
  return Math.hypot(point.x - (start.x + ratio * dx), point.y - (start.y + ratio * dy));
};

const overpassTracePath = (
  points: RaceLinePoint[], startRaceTimeMs: number, raceTimeMs: number,
  overpass: CourseOverpass | null, projection: Projection | null,
): string => {
  if (!overpass || !projection) return "";
  const start = Math.max(0, lastPointIndexAtTime(points, startRaceTimeMs));
  const end = lastPointIndexAtTime(points, raceTimeMs);
  if (end < start) return "";
  let path = "";
  let previousIncluded = false;
  for (let index = start; index <= end; index += 1) {
    const point = points[index];
    const source = { x: point.x_m, y: point.y_m };
    const nearBridge = overpass.points.slice(0, -1).some((bridgePoint, bridgeIndex) =>
      distanceToSegment(source, bridgePoint, overpass.points[bridgeIndex + 1]) <= 24);
    const before = points[Math.max(start, index - 1)];
    const after = points[Math.min(end, index + 1)];
    const travelX = after.x_m - before.x_m, travelY = after.y_m - before.y_m;
    const travelLength = Math.hypot(travelX, travelY);
    const aligned = travelLength > 1e-9
      && Math.abs((travelX * overpass.direction.x + travelY * overpass.direction.y) / travelLength) >= 0.72;
    const included = nearBridge && aligned;
    if (included) {
      const projected = projection.point(point);
      path += `${!previousIncluded || point.segment_break ? "M" : "L"}${projected.x.toFixed(1)},${projected.y.toFixed(1)} `;
    }
    previousIncluded = included;
  }
  return path.trim();
};

const buildProjection = (
  course: { x: number; y: number }[], actual: RaceLinePoint[],
): Projection | null => {
  // The viewport is owned by recorded GPS/course coordinates.  Observation
  // points use the same physical metre frame and therefore never resize it.
  const points = [...course.map(point => ({ race_time_ms: 0, x_m: point.x, y_m: point.y })), ...actual];
  if (!points.length) return null;
  const xs = points.map(point => point.x_m);
  const ys = points.map(point => point.y_m);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const width = Math.max(1, maxX - minX), height = Math.max(1, maxY - minY);
  const scale = Math.min(920 / width, 520 / height);
  const offsetX = (1000 - width * scale) / 2;
  const offsetY = (600 - height * scale) / 2;
  return {
    scale,
    point: source => ({
      x: offsetX + (source.x_m - minX) * scale,
      y: 600 - (offsetY + (source.y_m - minY) * scale),
    }),
  };
};

const interpolatePointAtTime = (
  points: RaceLinePoint[], raceTimeMs: number,
): RaceLinePoint | null => {
  const beforeIndex = lastPointIndexAtTime(points, raceTimeMs);
  if (beforeIndex < 0) return null;
  const before = points[beforeIndex];
  const after = points[beforeIndex + 1];
  if (!after || after.segment_break || after.race_time_ms <= before.race_time_ms) return before;
  const ratio = Math.max(0, Math.min(1,
    (raceTimeMs - before.race_time_ms) / (after.race_time_ms - before.race_time_ms),
  ));
  return {
    race_time_ms: raceTimeMs,
    x_m: before.x_m + (after.x_m - before.x_m) * ratio,
    y_m: before.y_m + (after.y_m - before.y_m) * ratio,
    causal_id: before.causal_id,
  };
};

interface FadingTrailSegment {
  key: number;
  path: string;
  overpassPath: string;
  opacity: number;
}

const buildFadingTrail = (
  points: RaceLinePoint[], raceTimeMs: number, projection: Projection | null,
  overpass: CourseOverpass | null,
): FadingTrailSegment[] => {
  if (!projection || !points.length) return [];
  const lifetimeMs = 40_000;
  const segmentMs = 1_000;
  const firstTime = raceTimeMs - lifetimeMs;
  const firstIndex = Math.max(0, lastPointIndexAtTime(points, firstTime));
  const lastIndex = lastPointIndexAtTime(points, raceTimeMs);
  if (lastIndex < firstIndex) return [];
  const buckets = new Map<number, RaceLinePoint[]>();
  for (let index = firstIndex; index <= lastIndex; index += 1) {
    const point = points[index];
    const key = Math.floor(point.race_time_ms / segmentMs) * segmentMs;
    const bucket = buckets.get(key) ?? [];
    if (!bucket.length && index > 0 && !point.segment_break) bucket.push(points[index - 1]);
    bucket.push(point);
    buckets.set(key, bucket);
  }
  return [...buckets.entries()].map(([key, bucket]) => {
    const endTime = Math.min(raceTimeMs, key + segmentMs);
    const ageMs = Math.max(0, raceTimeMs - endTime);
    const path = bucket.map((point, index) => {
      const projected = projection.point(point);
      return `${index === 0 || point.segment_break ? "M" : "L"}${projected.x.toFixed(1)},${projected.y.toFixed(1)}`;
    }).join(" ");
    return {
      key,
      path,
      overpassPath: overpassTracePath(bucket, bucket[0].race_time_ms,
        bucket.at(-1)!.race_time_ms, overpass, projection),
      opacity: Math.max(0, Math.min(1, 1 - ageMs / lifetimeMs)),
    };
  }).filter(segment => segment.opacity > 0);
};

const MODE_LABELS: readonly [RaceLineMode, TranslationKey][] = [
  ["actual", "line.actualOnly"], ["generated", "line.generatedOnly"], ["both", "line.both"],
];

const projectedForecast = (points: ForecastPoint[], projection: Projection | null) =>
  projection ? points.map(point => projection.point({
    race_time_ms: 0, x_m: point.x_m, y_m: point.y_m,
  })) : [];

const FORECAST_VECTOR_DISPLAY_SCALE = 1.12;

function SmoothForecastPath({
  points, projection, className, markerEnd,
}: {
  points: ForecastPoint[];
  projection: Projection | null;
  className: string;
  markerEnd: string;
}) {
  const pathRef = useRef<SVGPathElement | null>(null);
  const displayedRef = useRef<{ x: number; y: number }[]>([]);
  const animationRef = useRef<number | null>(null);
  const target = useMemo(() => {
    const projected = projectedForecast(points, projection);
    const origin = projected[0];
    return origin ? projected.map(point => ({
      x: (point.x - origin.x) * FORECAST_VECTOR_DISPLAY_SCALE,
      y: (point.y - origin.y) * FORECAST_VECTOR_DISPLAY_SCALE,
    })) : [];
  }, [points, projection]);

  useLayoutEffect(() => {
    if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
    if (!target.length) {
      displayedRef.current = [];
      pathRef.current?.setAttribute("d", "");
      return;
    }
    const from = displayedRef.current.length === target.length
      ? displayedRef.current.map(point => ({ ...point }))
      : target.map(point => ({ ...point }));
    displayedRef.current = from;
    const startedAt = performance.now();
    const durationMs = 150;
    const draw = (timestamp: number) => {
      const ratio = Math.max(0, Math.min(1, (timestamp - startedAt) / durationMs));
      const eased = ratio * ratio * (3 - 2 * ratio);
      const current = target.map((point, index) => ({
        x: from[index].x + (point.x - from[index].x) * eased,
        y: from[index].y + (point.y - from[index].y) * eased,
      }));
      displayedRef.current = current;
      if (pathRef.current) {
        pathRef.current.setAttribute("d", current.map((point, index) =>
          `${index === 0 ? "M" : "L"}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" "));
      }
      if (ratio < 1) animationRef.current = requestAnimationFrame(draw);
      else animationRef.current = null;
    };
    animationRef.current = requestAnimationFrame(draw);
    return () => {
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
    };
  }, [target]);

  return <path ref={pathRef} className={className} d="" markerEnd={markerEnd}
    data-display-scale={FORECAST_VECTOR_DISPLAY_SCALE} />;
}

function SmoothDynamicAnchor({
  points, raceTimeMs, trailEndRaceTimeMs, playState, projection, showMarker, children,
}: {
  points: RaceLinePoint[];
  raceTimeMs: number;
  trailEndRaceTimeMs: number;
  playState: PlayState;
  projection: Projection | null;
  showMarker: boolean;
  children: React.ReactNode;
}) {
  const groupRef = useRef<SVGGElement | null>(null);
  const tipRef = useRef<SVGPathElement | null>(null);
  const stateRef = useRef({ raceTimeMs, trailEndRaceTimeMs, playState, changedAt: performance.now() });
  const drawAtTime = (visualTime: number) => {
    const source = interpolatePointAtTime(points, visualTime);
    const projected = source && projection ? projection.point(source) : null;
    if (!projected) return;
    if (groupRef.current) {
      groupRef.current.setAttribute("transform", `translate(${projected.x.toFixed(2)} ${projected.y.toFixed(2)})`);
      groupRef.current.dataset.visualRaceTimeMs = String(Math.round(visualTime));
    }
    const tip = tipRef.current;
    const trailIndex = lastPointIndexAtTime(points, stateRef.current.trailEndRaceTimeMs);
    const trailPoint = trailIndex >= 0 ? points[trailIndex] : null;
    const trailProjected = trailPoint && projection ? projection.point(trailPoint) : null;
    if (tip && trailProjected) {
      tip.setAttribute("d", `M${trailProjected.x.toFixed(2)},${trailProjected.y.toFixed(2)} L${projected.x.toFixed(2)},${projected.y.toFixed(2)}`);
      tip.dataset.visualRaceTimeMs = String(Math.round(visualTime));
      tip.dataset.endX = projected.x.toFixed(2);
      tip.dataset.endY = projected.y.toFixed(2);
    }
  };
  useLayoutEffect(() => {
    stateRef.current = { raceTimeMs, trailEndRaceTimeMs, playState, changedAt: performance.now() };
    drawAtTime(raceTimeMs);
  }, [points, projection, raceTimeMs, trailEndRaceTimeMs, playState]);
  useEffect(() => {
    let frame = 0;
    const draw = (timestamp: number) => {
      const state = stateRef.current;
      const visualTime = state.playState === "playing"
        ? state.raceTimeMs + Math.min(120, timestamp - state.changedAt)
        : state.raceTimeMs;
      drawAtTime(visualTime);
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [points, projection]);
  const initial = interpolatePointAtTime(points, raceTimeMs);
  const projected = initial && projection ? projection.point(initial) : null;
  if (!projected) return null;
  return <>
    {showMarker && <defs>
      <path ref={tipRef} id="race-line-actual-tip-path" className="race-line-actual-tip-source" d="" />
    </defs>}
    <g ref={groupRef} className="race-line-dynamic-anchor"
      transform={`translate(${projected.x.toFixed(2)} ${projected.y.toFixed(2)})`}>
      {children}
      {showMarker && <circle className="race-line-marker-actual" cx="0" cy="0" r="5" />}
    </g>
  </>;
}

export function RaceLinesPanel({
  carId, raceTimeMs, seekGeneration, telemetryRecord, playState, dataFetchEnabled = true,
  initialMode = "both",
}: {
  carId: CarId;
  raceTimeMs: number;
  seekGeneration: number;
  telemetryRecord: Stage2TelemetryRecord | null;
  playState: PlayState;
  dataFetchEnabled?: boolean;
  initialMode?: RaceLineMode;
}) {
  const { t, formatNumber } = useAppLanguage();
  const { asset, status, error } = useRaceLineAsset(carId, seekGeneration, dataFetchEnabled);
  const [mode, setMode] = useState<RaceLineMode>(initialMode);
  const lapRange = useMemo(() => {
    if (!asset) return null;
    return asset.lap_ranges.find(range => range.start_race_time_ms <= raceTimeMs
      && raceTimeMs <= range.end_race_time_ms)
      ?? (raceTimeMs < asset.lap_ranges[0].start_race_time_ms ? asset.lap_ranges[0] : asset.lap_ranges.at(-1) ?? null);
  }, [asset, raceTimeMs]);
  const projection = useMemo(
    () => asset ? buildProjection(asset.course.points, asset.actual.points) : null,
    [asset],
  );
  const currentTelemetry = telemetryRecord?.car_id === carId
    && telemetryRecord.seek_generation === seekGeneration ? telemetryRecord : null;
  const telemetryGap = currentTelemetry?.telemetry_status.available === false
    ? currentTelemetry.telemetry_status : null;
  const dynamicTelemetry = telemetryGap ? null : currentTelemetry;
  const forecast = useMemo(
    () => dynamicTelemetry ? buildRaceLineForecast(dynamicTelemetry) : null,
    [dynamicTelemetry],
  );
  const overpass = useMemo(
    () => asset ? findSuzukaOverpass(asset.course.points) : null,
    [asset],
  );
  const coursePath = useMemo(() => asset && projection
    ? asset.course.points.map((point, index) => {
      const projected = projection.point({ race_time_ms: 0, x_m: point.x, y_m: point.y });
      return `${index === 0 ? "M" : "L"}${projected.x.toFixed(1)},${projected.y.toFixed(1)}`;
    }).join(" ") + " Z"
    : "", [asset, projection]);
  const overpassPath = useMemo(() => overpass && projection
    ? overpass.points.map((point, index) => {
      const projected = projection.point({ race_time_ms: 0, x_m: point.x, y_m: point.y });
      return `${index === 0 ? "M" : "L"}${projected.x.toFixed(1)},${projected.y.toFixed(1)}`;
    }).join(" ")
    : "", [overpass, projection]);
  const overpassCutoutPath = useMemo(() => overpass && projection
    ? overpass.cutoutPoints.map((point, index) => {
      const projected = projection.point({ race_time_ms: 0, x_m: point.x, y_m: point.y });
      return `${index === 0 ? "M" : "L"}${projected.x.toFixed(1)},${projected.y.toFixed(1)}`;
    }).join(" ")
    : "", [overpass, projection]);
  const trailTime = Math.floor(raceTimeMs / 100) * 100;
  const actualTrail = useMemo(
    () => buildFadingTrail(asset?.actual.points ?? [], trailTime, projection, overpass),
    [asset, trailTime, projection, overpass],
  );
  const generatedTrail = useMemo(
    () => buildFadingTrail(asset?.generated.points ?? [], trailTime, projection, overpass),
    [asset, trailTime, projection, overpass],
  );
  const courseWidth = asset && projection ? asset.course.width.median_m * projection.scale : 0;
  const courseOutlineWidth = courseWidth + 4;
  const overpassCutoutWidth = courseOutlineWidth + 4;

  return (
    <section
      className="race-lines-panel"
      data-testid="race-lines-panel"
      data-car-id={carId}
      data-race-time-ms={raceTimeMs}
      data-seek-generation={seekGeneration}
      data-line-mode={mode}
      data-status={status}
      data-telemetry-available={telemetryGap ? "false" : "true"}
      data-generated-causal-id={dynamicTelemetry?.shadow_causal_id ?? ""}
      data-generated-display-transform="classical-driver-model-output-lookahead-rollout"
      data-generated-trail-segments={generatedTrail.length}
    >
      <header>
        <div>
          <span>SUZUKA TRAJECTORY</span>
          <strong>RACING LINE</strong>
          <small className="race-lines-lap-time" data-testid="race-lines-lap-time">
            {telemetryGap ? "SOURCE GAP" : status === "ready"
              ? `LAP ${lapRange?.lap_counter ?? "–"} · ${Math.round(raceTimeMs / 100) / 10} s`
              : t("line.loading")}
          </small>
        </div>
        <nav aria-label={t("line.toggle")}>
          {MODE_LABELS.map(([value, label]) => (
            <button key={value} type="button" aria-pressed={mode === value} onClick={() => setMode(value)}>{t(label)}</button>
          ))}
        </nav>
      </header>
      {error && <p className="race-lines-error" role="alert">{error}</p>}
      <svg viewBox="0 0 1000 600" role="img" aria-label={t("line.aria")}>
        <defs>
          <marker id="race-line-driver-arrowhead" viewBox="0 0 10 10"
            refX="8.5" refY="5" markerWidth="10" markerHeight="10"
            markerUnits="userSpaceOnUse" orient="auto">
            <path className="race-line-driver-arrowhead" d="M0 0L10 5L0 10Z" />
          </marker>
          <marker id="race-line-vehicle-arrowhead" viewBox="0 0 10 10"
            refX="8.5" refY="5" markerWidth="10" markerHeight="10"
            markerUnits="userSpaceOnUse" orient="auto">
            <path className="race-line-vehicle-arrowhead" d="M0 0L10 5L0 10Z" />
          </marker>
        </defs>
        <path className="race-line-course-outline" d={coursePath} style={{ strokeWidth: courseOutlineWidth }} />
        <path className="race-line-course" d={coursePath} style={{ strokeWidth: courseWidth }} />
        {!telemetryGap && (mode === "actual" || mode === "both") && actualTrail.map(segment =>
          <path key={segment.key} className="race-line-actual" d={segment.path} opacity={segment.opacity} />)}
        {!telemetryGap && (mode === "actual" || mode === "both") &&
          <use className="race-line-actual-tip" href="#race-line-actual-tip-path" />}
        {!telemetryGap && (mode === "generated" || mode === "both") && generatedTrail.map(segment =>
          <path key={`generated-${segment.key}`} className="race-line-generated"
            d={segment.path} opacity={segment.opacity} />)}
        <path className="race-line-overpass-cutout" d={overpassCutoutPath}
          style={{ strokeWidth: overpassCutoutWidth }} />
        <path className="race-line-overpass-outline" d={overpassPath}
          style={{ strokeWidth: courseOutlineWidth }} />
        <path className="race-line-overpass" d={overpassPath} style={{ strokeWidth: courseWidth }} />
        {!telemetryGap && (mode === "actual" || mode === "both") && actualTrail.map(segment =>
          <path key={`overpass-${segment.key}`} className="race-line-actual"
            d={segment.overpassPath} opacity={segment.opacity} />)}
        {!telemetryGap && (mode === "generated" || mode === "both") && generatedTrail.map(segment =>
          <path key={`generated-overpass-${segment.key}`} className="race-line-generated"
            d={segment.overpassPath} opacity={segment.opacity} />)}
        {!telemetryGap && (mode === "actual" || mode === "generated" || mode === "both") &&
          <SmoothDynamicAnchor points={asset?.actual.points ?? []} raceTimeMs={raceTimeMs}
            trailEndRaceTimeMs={trailTime} playState={playState} projection={projection}
            showMarker={mode === "actual" || mode === "both"}>
            {(mode === "generated" || mode === "both") && <>
              <SmoothForecastPath points={forecast?.vehicleResponse ?? []} projection={projection}
                className="race-line-vehicle-response"
                markerEnd="url(#race-line-vehicle-arrowhead)" />
              <SmoothForecastPath points={forecast?.driverIntent ?? []} projection={projection}
                className="race-line-driver-intent"
                markerEnd="url(#race-line-driver-arrowhead)" />
            </>}
          </SmoothDynamicAnchor>}
      </svg>
      {telemetryGap && <div className="race-lines-no-telemetry" role="status" data-testid="no-telemetry-data">
        <span>NO TELEMETRY DATA</span>
        <small>{formatNumber(telemetryGap.previous_race_time_ms / 1000, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} s — {formatNumber(telemetryGap.next_race_time_ms / 1000, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} s</small>
      </div>}
      <footer>
        <div className="race-lines-legend">
          <span><i className="race-line-key-actual" />{t("line.actual")}</span>
          <span><i className="race-line-key-generated" />{t("line.predicted")}</span>
          <span><i className="race-line-key-driver-intent" />{t("line.driverIntent")}</span>
          <span><i className="race-line-key-vehicle-response" />{t("line.vehicleResponse")}</span>
        </div>
      </footer>
    </section>
  );
}
